import React, { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { Search, SlidersHorizontal, X, ChevronDown, Package, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { useAuthStore } from "@/store/auth.store"
import { searchBuyers } from "@/lib/api/buyer.api"
import { searchCompanies } from "@/lib/api/company.api"
import { DatePicker } from "@/components/ui/date-picker"

export interface QuotationFiltersProps {
  status: string
  setStatus: (status: string) => void
  preferredShipmentMode?: string
  setPreferredShipmentMode?: (mode: string) => void
  fromDate?: string
  setFromDate?: (date: string) => void
  toDate?: string
  setToDate?: (date: string) => void
  isLatest?: boolean
  setIsLatest?: (val: boolean) => void
  setCurrentPage: (page: number) => void
  totalCount: number
  showAdvancedFilters?: boolean
  onClearFilters: () => void
  statusOptions?: { value: string; label: string }[]
  buyerId?: string
  setBuyerId?: (buyerId: string, buyerName?: string) => void
  buyerName?: string
  setBuyerName?: (buyerName: string) => void
  companyId?: string
  setCompanyId?: (companyId: string, companyName?: string) => void
  companyName?: string
  setCompanyName?: (companyName: string) => void
}

const DEFAULT_STATUS_OPTIONS = [
  { value: "PENDING", label: "PENDING" },
  { value: "ACCEPTED", label: "ACCEPTED" },
  { value: "REJECTED", label: "REJECTED" },
  { value: "REQUEST_REVISION", label: "REQUEST REVISION" },
  { value: "SUPERSEDED", label: "SUPERSEDED" },
  { value: "AWAITING_REVISED_QUOTATION", label: "AWAITING REVISED QUOTATION" },
]

export function QuotationFilters({
  status,
  setStatus,
  fromDate = "",
  setFromDate,
  toDate = "",
  setToDate,
  isLatest = false,
  setIsLatest,
  setCurrentPage,
  totalCount,
  showAdvancedFilters = true,
  onClearFilters,
  statusOptions = DEFAULT_STATUS_OPTIONS,
  buyerId = "",
  setBuyerId,
  buyerName = "",
  setBuyerName,
  companyId = "",
  setCompanyId,
  companyName = "",
  setCompanyName,
}: QuotationFiltersProps) {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false)
  const token = useAuthStore((s) => s.accessToken)
  const userRole = useAuthStore((s) => s.user?.role)

  // Search Buyer states
  const [buyerSearchQuery, setBuyerSearchQuery] = useState(buyerName || "")
  const [buyerDisplayName, setBuyerDisplayName] = useState(buyerName || "") // frozen label for pill - only set on selection
  const [buyerSearchResults, setBuyerSearchResults] = useState<any[]>([])
  const [isBuyerSearchDropdownOpen, setIsBuyerSearchDropdownOpen] = useState(false)
  const [buyerSearchEmpty, setBuyerSearchEmpty] = useState(false)
  const [isBuyerSearchLoading, setIsBuyerSearchLoading] = useState(false)
  const buyerAbortRef = useRef<AbortController | null>(null)
  const isBuyerSelectionRef = useRef(false)

  // Search Company states
  const [companySearchQuery, setCompanySearchQuery] = useState(companyName || "")
  const [companyDisplayName, setCompanyDisplayName] = useState(companyName || "") // frozen label for pill - only set on selection
  const [companySearchResults, setCompanySearchResults] = useState<any[]>([])
  const [isCompanySearchDropdownOpen, setIsCompanySearchDropdownOpen] = useState(false)
  const [companySearchEmpty, setCompanySearchEmpty] = useState(false)
  const [isCompanySearchLoading, setIsCompanySearchLoading] = useState(false)
  const companyAbortRef = useRef<AbortController | null>(null)
  const isCompanySelectionRef = useRef(false)

  // Sync internal display & search state when external buyerName/buyerId changes
  useEffect(() => {
    if (buyerName) {
      setBuyerDisplayName(buyerName)
      setBuyerSearchQuery(buyerName)
    } else if (!buyerId) {
      setBuyerDisplayName("")
      setBuyerSearchQuery("")
    }
  }, [buyerName, buyerId])

  // Sync internal display & search state when external companyName/companyId changes
  useEffect(() => {
    if (companyName) {
      setCompanyDisplayName(companyName)
      setCompanySearchQuery(companyName)
    } else if (!companyId) {
      setCompanyDisplayName("")
      setCompanySearchQuery("")
    }
  }, [companyName, companyId])

  // Debounce 800ms for buyer search with request cancellation
  useEffect(() => {
    if (isBuyerSelectionRef.current) {
      isBuyerSelectionRef.current = false
      return
    }

    const val = buyerSearchQuery.trim()
    // If input matches already selected buyer display name, do not search or open dropdown
    if (buyerDisplayName && val === buyerDisplayName.trim()) {
      setIsBuyerSearchLoading(false)
      setIsBuyerSearchDropdownOpen(false)
      return
    }

    if (val.length < 2) {
      setBuyerSearchResults([])
      setBuyerSearchEmpty(false)
      setIsBuyerSearchDropdownOpen(false)
      setIsBuyerSearchLoading(false)
      buyerAbortRef.current?.abort()
      return
    }

    setIsBuyerSearchLoading(true)
    const timer = setTimeout(async () => {
      // Cancel previous in-flight request
      buyerAbortRef.current?.abort()
      const controller = new AbortController()
      buyerAbortRef.current = controller

      try {
        const res = await searchBuyers(val, token!, controller.signal)
        // Only update UI if request was not superseded
        if (!controller.signal.aborted) {
          const results = res || []
          setBuyerSearchResults(results)
          setBuyerSearchEmpty(results.length === 0)
          setIsBuyerSearchDropdownOpen(true)
        }
      } catch (err: any) {
        if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
          console.error(err)
        }
      } finally {
        if (!controller.signal.aborted) setIsBuyerSearchLoading(false)
      }
    }, 800)

    return () => {
      clearTimeout(timer)
    }
  }, [buyerSearchQuery, buyerDisplayName, token])

  // Debounce 800ms for company search with request cancellation
  useEffect(() => {
    if (isCompanySelectionRef.current) {
      isCompanySelectionRef.current = false
      return
    }

    const val = companySearchQuery.trim()
    // If input matches already selected company display name, do not search or open dropdown
    if (companyDisplayName && val === companyDisplayName.trim()) {
      setIsCompanySearchLoading(false)
      setIsCompanySearchDropdownOpen(false)
      return
    }

    if (val.length < 2) {
      setCompanySearchResults([])
      setCompanySearchEmpty(false)
      setIsCompanySearchDropdownOpen(false)
      setIsCompanySearchLoading(false)
      companyAbortRef.current?.abort()
      return
    }

    setIsCompanySearchLoading(true)
    const timer = setTimeout(async () => {
      // Cancel previous in-flight request
      companyAbortRef.current?.abort()
      const controller = new AbortController()
      companyAbortRef.current = controller

      try {
        const res = await searchCompanies(val, token!, controller.signal)
        // Only update UI if request was not superseded
        if (!controller.signal.aborted) {
          const results = res || []
          setCompanySearchResults(results)
          setCompanySearchEmpty(results.length === 0)
          setIsCompanySearchDropdownOpen(true)
        }
      } catch (err: any) {
        if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
          console.error(err)
        }
      } finally {
        if (!controller.signal.aborted) setIsCompanySearchLoading(false)
      }
    }, 800)

    return () => {
      clearTimeout(timer)
    }
  }, [companySearchQuery, companyDisplayName, token])

  // Cleanup abort controllers on unmount
  useEffect(() => {
    return () => {
      buyerAbortRef.current?.abort()
      companyAbortRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    if (!isFilterDrawerOpen) return

    const handleScroll = () => {
      setIsFilterDrawerOpen(false)
      setIsBuyerSearchDropdownOpen(false)
      setIsCompanySearchDropdownOpen(false)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [isFilterDrawerOpen])

  const hasAdvancedFiltersActive =
    (fromDate || toDate || buyerId || companyId || isLatest === true)

  const handleClearAdvancedFilters = () => {
    if (setFromDate) setFromDate("")
    if (setToDate) setToDate("")
    if (setIsLatest) setIsLatest(false)
    if (setBuyerId) setBuyerId("")
    if (setBuyerName) setBuyerName("")
    if (setCompanyId) setCompanyId("")
    if (setCompanyName) setCompanyName("")
    buyerAbortRef.current?.abort()
    companyAbortRef.current?.abort()
    setBuyerSearchQuery("")
    setBuyerDisplayName("")
    setCompanySearchQuery("")
    setCompanyDisplayName("")
    setBuyerSearchResults([])
    setBuyerSearchEmpty(false)
    setCompanySearchResults([])
    setCompanySearchEmpty(false)
    setIsBuyerSearchDropdownOpen(false)
    setIsCompanySearchDropdownOpen(false)
    setCurrentPage(1)
    setIsFilterDrawerOpen(false)
  }

  const isStatusCleared = status === ""
  const hasAnyFilterActive = !isStatusCleared || hasAdvancedFiltersActive

  return (
    <>
      <div className="bg-white p-3 border-[0.5px] border-slate-200/60 rounded-xl shadow-sm flex flex-wrap items-center gap-3">
        {/* Search Company Filter (Inline - Leftmost - Expandable & Responsive) */}
        {setCompanyId && (
          <div className="relative font-sans w-full sm:w-auto sm:flex-1 sm:max-w-[400px]">
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200/80 px-3 py-1.5 rounded-lg shadow-xs focus-within:border-[#be001e]/50 transition-all">
              <input 
                type="text" 
                placeholder="Search company..."
                value={companySearchQuery}
                onChange={(e) => {
                  setCompanySearchQuery(e.target.value)
                }}
                className="w-full text-xs font-semibold text-slate-700 border-none focus:ring-0 p-0 outline-none bg-transparent placeholder-slate-450"
              />
              {(companyId || companySearchQuery) && (
                <button
                  type="button"
                  onClick={() => {
                    companyAbortRef.current?.abort()
                    setCompanyId("")
                    setCompanyName?.("")
                    setCompanySearchQuery("")
                    setCompanyDisplayName("")
                    setCompanySearchResults([])
                    setCompanySearchEmpty(false)
                    setIsCompanySearchLoading(false)
                    setIsCompanySearchDropdownOpen(false)
                    setCurrentPage(1)
                  }}
                  className="text-[#be001e] hover:text-[#be001e]/80 cursor-pointer"
                >
                  {isCompanySearchLoading
                    ? <Loader2 className="size-3.5 animate-spin text-[#be001e]" />
                    : <X className="size-3.5 text-[#be001e]" />}
                </button>
              )}
            </div>
            {isCompanySearchDropdownOpen && companySearchResults.length > 0 && (
              <>
                <div 
                  className="fixed inset-0 z-30" 
                  onClick={() => setIsCompanySearchDropdownOpen(false)}
                />
                <div className="absolute left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-1.5 z-40 max-h-[150px] overflow-y-auto scrollbar-thin">
                  {companySearchResults.map((comp: any) => (
                    <button
                      key={comp.id}
                      type="button"
                      onClick={() => {
                        const selectedName = comp.name || comp.legalName || comp.id
                        isCompanySelectionRef.current = true
                        setCompanyId(comp.id, selectedName)
                        setCompanyName?.(selectedName)
                        setCompanyDisplayName(selectedName)
                        setCompanySearchQuery(selectedName)
                        setCompanySearchResults([])
                        setCompanySearchEmpty(false)
                        setIsCompanySearchLoading(false)
                        setIsCompanySearchDropdownOpen(false)
                        setCurrentPage(1)
                      }}
                      className={cn(
                        "w-full text-left px-3 py-1.5 text-xs transition-colors hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer block font-semibold",
                        companyId === comp.id ? "text-[#be001e] bg-[#be001e]/5" : "text-slate-700"
                      )}
                    >
                      {comp.name}
                    </button>
                  ))}
                </div>
              </>
            )}
            {isCompanySearchDropdownOpen && companySearchEmpty && (
              <div className="absolute left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-2 z-40 flex flex-col items-center gap-1.5">
                <p className="text-[11px] font-bold text-slate-500">No company found</p>
              </div>
            )}
          </div>
        )}

        {/* Search Buyer Filter (Inline - Next to Company - Expandable & Responsive) */}
        {setBuyerId && (
          <div className="relative font-sans w-full sm:w-auto sm:flex-1 sm:max-w-[400px]">
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200/80 px-3 py-1.5 rounded-lg shadow-xs focus-within:border-[#be001e]/50 transition-all">
              <input 
                type="text" 
                placeholder="Search buyer..."
                value={buyerSearchQuery}
                onChange={(e) => {
                  setBuyerSearchQuery(e.target.value)
                }}
                className="w-full text-xs font-semibold text-slate-700 border-none focus:ring-0 p-0 outline-none bg-transparent placeholder-slate-450"
              />
              {(buyerId || buyerSearchQuery) && (
                <button
                  type="button"
                  onClick={() => {
                    buyerAbortRef.current?.abort()
                    setBuyerId("")
                    setBuyerName?.("")
                    setBuyerSearchQuery("")
                    setBuyerDisplayName("")
                    setBuyerSearchResults([])
                    setBuyerSearchEmpty(false)
                    setIsBuyerSearchLoading(false)
                    setIsBuyerSearchDropdownOpen(false)
                    setCurrentPage(1)
                  }}
                  className="text-[#be001e] hover:text-[#be001e]/80 cursor-pointer"
                >
                  {isBuyerSearchLoading
                    ? <Loader2 className="size-3.5 animate-spin text-[#be001e]" />
                    : <X className="size-3.5 text-[#be001e]" />}
                </button>
              )}
            </div>
            {isBuyerSearchDropdownOpen && buyerSearchResults.length > 0 && (
              <>
                <div 
                  className="fixed inset-0 z-30" 
                  onClick={() => setIsBuyerSearchDropdownOpen(false)}
                />
                <div className="absolute left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-1.5 z-40 max-h-[150px] overflow-y-auto scrollbar-thin">
                  {buyerSearchResults.map((buyer: any) => {
                    const targetBuyerId = buyer.id
                    const displayName = buyer.user?.name || buyer.name || buyer.email || targetBuyerId
                    return (
                      <button
                        key={buyer.id}
                        type="button"
                        onClick={() => {
                          isBuyerSelectionRef.current = true
                          setBuyerId(targetBuyerId, displayName)
                          setBuyerName?.(displayName)
                          setBuyerDisplayName(displayName)
                          setBuyerSearchQuery(displayName)
                          setBuyerSearchResults([])
                          setBuyerSearchEmpty(false)
                          setIsBuyerSearchLoading(false)
                          setIsBuyerSearchDropdownOpen(false)
                          setCurrentPage(1)
                        }}
                        className={cn(
                          "w-full text-left px-3 py-1.5 text-xs transition-colors hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer block font-semibold",
                          buyerId === targetBuyerId ? "text-[#be001e] bg-[#be001e]/5" : "text-slate-700"
                        )}
                      >
                        {displayName}
                      </button>
                    )
                  })}
                </div>
              </>
            )}
            {isBuyerSearchDropdownOpen && buyerSearchEmpty && (
              <div className="absolute left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-2 z-40 flex flex-col items-center gap-1.5">
                <p className="text-[11px] font-bold text-slate-500">No buyer found</p>
              </div>
            )}
          </div>
        )}

        {/* Second Row on Mobile: Status Dropdown & Filters Toggle */}
        <div className="flex items-center justify-between w-full sm:w-auto sm:contents">
          {/* Status Dropdown */}
          <div className="relative font-sans text-left">
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className="w-[110px] bg-slate-50 border-[0.5px] border-slate-200/80 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#be001e] hover:bg-slate-100 transition-colors flex items-center justify-between cursor-pointer"
            >
              <span>
                {status
                  ? statusOptions.find((o) => o.value === status)?.label || status
                  : "Status"}
              </span>
              <ChevronDown className={cn("size-3.5 transition-transform duration-200", isStatusDropdownOpen && "rotate-180")} />
            </button>

            {isStatusDropdownOpen && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setIsStatusDropdownOpen(false)}
                />
                <div className="absolute right-0 sm:left-0 mt-1 w-[130px] bg-white border border-slate-200/80 rounded-lg shadow-lg py-1.5 z-[35] max-h-[200px] overflow-y-auto scrollbar-thin">
                  {statusOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setStatus(opt.value)
                        setCurrentPage(1)
                        setIsStatusDropdownOpen(false)
                      }}
                      className={cn(
                        "w-full text-left px-3 py-1.5 text-xs transition-colors hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer block font-semibold",
                        status === opt.value ? "text-[#be001e] bg-[#be001e]/5" : "text-slate-700"
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Filters Toggle Button (Far Right) */}
          <div className="sm:ml-auto flex items-center gap-3">
            {showAdvancedFilters && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsFilterDrawerOpen(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border-[0.5px] border-slate-200/80 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  <SlidersHorizontal className="size-4 text-slate-500" />
                  <span className="hidden sm:inline text-xs font-bold text-slate-700">Filters</span>
                </button>
              </div>
            )}

            {!showAdvancedFilters && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border-[0.5px] border-slate-200/80 rounded-lg">
                <SlidersHorizontal className="size-4 text-slate-500" />
                <span className="hidden sm:inline text-xs font-bold text-slate-700">Filters</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Active Filter Pills area */}
      {hasAnyFilterActive && (
        <div className="flex flex-wrap items-center gap-2 mt-2 px-1">
          {status && (
            <div className="flex items-center gap-1.5 bg-[#be001e]/5 border border-[#be001e]/20 rounded-md pl-2.5 pr-1.5 py-1 text-[11px] text-[#be001e] font-medium transition-colors hover:bg-[#be001e]/10">
              <span className="opacity-70">Status:</span> {statusOptions.find((o) => o.value === status)?.label || status}
              <button 
                onClick={() => {
                  setStatus("")
                  setCurrentPage(1)
                }} 
                className="p-0.5 hover:bg-[#be001e]/20 rounded-full ml-1 transition-colors cursor-pointer"
              >
                <X className="size-3" />
              </button>
            </div>
          )}

          {/* Buyer Pill */}
          {buyerId && setBuyerId && (
            <div className="flex items-center gap-1.5 bg-[#be001e]/5 border border-[#be001e]/20 rounded-md pl-2.5 pr-1.5 py-1 text-[11px] text-[#be001e] font-medium transition-colors hover:bg-[#be001e]/10">
              <span className="opacity-70">Buyer:</span> {buyerDisplayName || buyerName || buyerId}
              <button 
                onClick={() => {
                  buyerAbortRef.current?.abort()
                  setBuyerId("")
                  setBuyerName?.("")
                  setBuyerSearchQuery("")
                  setBuyerDisplayName("")
                  setBuyerSearchResults([])
                  setBuyerSearchEmpty(false)
                  setIsBuyerSearchDropdownOpen(false)
                  setCurrentPage(1)
                }} 
                className="p-0.5 hover:bg-[#be001e]/20 rounded-full ml-1 transition-colors cursor-pointer"
              >
                <X className="size-3" />
              </button>
            </div>
          )}

          {/* Company Pill */}
          {companyId && setCompanyId && (
            <div className="flex items-center gap-1.5 bg-[#be001e]/5 border border-[#be001e]/20 rounded-md pl-2.5 pr-1.5 py-1 text-[11px] text-[#be001e] font-medium transition-colors hover:bg-[#be001e]/10">
              <span className="opacity-70">Company:</span> {companyDisplayName || companyName || companyId}
              <button 
                onClick={() => {
                  companyAbortRef.current?.abort()
                  setCompanyId("")
                  setCompanyName?.("")
                  setCompanySearchQuery("")
                  setCompanyDisplayName("")
                  setCompanySearchResults([])
                  setCompanySearchEmpty(false)
                  setIsCompanySearchDropdownOpen(false)
                  setCurrentPage(1)
                }} 
                className="p-0.5 hover:bg-[#be001e]/20 rounded-full ml-1 transition-colors cursor-pointer"
              >
                <X className="size-3" />
              </button>
            </div>
          )}

          {/* Is Latest Pill */}
          {setIsLatest && isLatest && (
            <div className="flex items-center gap-1.5 bg-[#be001e]/5 border border-[#be001e]/20 rounded-md pl-2.5 pr-1.5 py-1 text-[11px] text-[#be001e] font-medium transition-colors hover:bg-[#be001e]/10">
              <span className="opacity-70">Latest Version:</span> {isLatest ? "True" : "False"}
              <button 
                onClick={() => {
                  setIsLatest(false)
                  setCurrentPage(1)
                }} 
                className="p-0.5 hover:bg-[#be001e]/20 rounded-full ml-1 transition-colors cursor-pointer"
              >
                <X className="size-3" />
              </button>
            </div>
          )}

          {/* From Date Pill */}
          {fromDate && setFromDate && (
            <div className="flex items-center gap-1.5 bg-[#be001e]/5 border border-[#be001e]/20 rounded-md pl-2.5 pr-1.5 py-1 text-[11px] text-[#be001e] font-medium transition-colors hover:bg-[#be001e]/10">
              <span className="opacity-70">From:</span> {fromDate}
              <button 
                onClick={() => {
                  setFromDate("")
                  setCurrentPage(1)
                }} 
                className="p-0.5 hover:bg-[#be001e]/20 rounded-full ml-1 transition-colors cursor-pointer"
              >
                <X className="size-3" />
              </button>
            </div>
          )}

          {/* To Date Pill */}
          {toDate && setToDate && (
            <div className="flex items-center gap-1.5 bg-[#be001e]/5 border border-[#be001e]/20 rounded-md pl-2.5 pr-1.5 py-1 text-[11px] text-[#be001e] font-medium transition-colors hover:bg-[#be001e]/10">
              <span className="opacity-70">To:</span> {toDate}
              <button 
                onClick={() => {
                  setToDate("")
                  setCurrentPage(1)
                }} 
                className="p-0.5 hover:bg-[#be001e]/20 rounded-full ml-1 transition-colors cursor-pointer"
              >
                <X className="size-3" />
              </button>
            </div>
          )}

          {/* Clear All Link */}
          <button 
            onClick={() => {
              buyerAbortRef.current?.abort()
              companyAbortRef.current?.abort()
              setBuyerSearchQuery("")
              setBuyerDisplayName("")
              setBuyerSearchEmpty(false)
              setCompanySearchQuery("")
              setCompanyDisplayName("")
              setCompanySearchEmpty(false)
              setBuyerSearchResults([])
              setCompanySearchResults([])
              onClearFilters()
            }}
            className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 hover:underline px-2 cursor-pointer self-center"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Advanced Filters Drawer */}
      {showAdvancedFilters && isFilterDrawerOpen && typeof document !== "undefined" && createPortal(
        <>
          {/* Backdrop Overlay */}
          <div
            className={cn(
              "fixed inset-x-0 bottom-0 z-[60] transition-opacity animate-in fade-in duration-200",
              (userRole?.toUpperCase() === "ADMIN" || userRole?.toUpperCase() === "SELLER") ? "top-12" : "top-16"
            )}
            onClick={() => {
              setIsFilterDrawerOpen(false)
              setIsBuyerSearchDropdownOpen(false)
              setIsCompanySearchDropdownOpen(false)
            }}
          />
          {/* Sliding Filter Sidebar */}
          <div className={cn(
            "fixed right-0 bottom-0 w-full max-w-[250px] bg-white z-[99] shadow-2xl flex flex-col justify-between animate-in slide-in-from-right duration-300 border-l border-slate-100",
            (userRole?.toUpperCase() === "ADMIN" || userRole?.toUpperCase() === "SELLER") ? "top-12" : "top-16"
          )}>
            
            {/* Drawer Body */}
            <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-4.5 scrollbar-thin">




              {/* Is Latest Toggle */}
              {setIsLatest && (
                <div className="space-y-2">
                  <h4 className="text-[11px] font-bold text-slate-800 uppercase tracking-wider px-2">
                    Latest Version
                  </h4>
                  <div className="px-2 py-1">
                    <button
                      type="button"
                      onClick={() => {
                        setIsLatest(!isLatest)
                        setCurrentPage(1)
                      }}
                      className={cn(
                        "text-[12px] px-3 py-1.5 rounded-xl text-left w-full transition-all focus:outline-none flex items-center gap-2.5 group cursor-pointer border",
                        isLatest
                          ? "bg-[#be001e]/5 border-[#be001e]/20 text-[#be001e] font-semibold"
                          : "bg-white border-slate-200 text-slate-800 hover:bg-slate-50"
                      )}
                    >
                      <div className={cn(
                        "size-3.5 rounded flex items-center justify-center transition-all shrink-0 border",
                        isLatest 
                          ? "border-[#be001e] bg-[#be001e] text-white" 
                          : "border-slate-300 bg-white group-hover:border-[#be001e]/50"
                      )}>
                        {isLatest && (
                          <svg className="size-2.5 stroke-[3]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span>Show Latest Only</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Date Range Section */}
              {(setFromDate || setToDate) && (
                <div className="space-y-2">
                  <h4 className="text-[11px] font-bold text-slate-800 uppercase tracking-wider px-2">
                    Date Range
                  </h4>
                  <div className="flex flex-col gap-3 px-1 pt-1">
                    {setFromDate && (
                      <div className="flex flex-col gap-1.5 text-left">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                          From Date
                        </label>
                        <DatePicker
                          value={fromDate}
                          onChange={(val) => {
                            setFromDate(val)
                            setCurrentPage(1)
                          }}
                          placeholder="Select from date"
                          placement="top"
                        />
                      </div>
                    )}
                    {setToDate && (
                      <div className="flex flex-col gap-1.5 text-left">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                          To Date
                        </label>
                        <DatePicker
                          value={toDate}
                          onChange={(val) => {
                            setToDate(val)
                            setCurrentPage(1)
                          }}
                          placeholder="Select to date"
                          placement="top"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Drawer Footer */}
            <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
              <button
                type="button"
                onClick={handleClearAdvancedFilters}
                className="text-[12px] font-semibold text-slate-500 hover:text-slate-800 hover:underline transition-colors px-1"
              >
                Reset All
              </button>
              <button
                type="button"
                onClick={() => setIsFilterDrawerOpen(false)}
                className="bg-[#be001e] hover:bg-[#be001e]/90 text-white font-bold text-[12px] px-5 py-1.5 rounded-lg shadow-sm transition active:scale-95 cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  )
}
